import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  ButtonGroup,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Description as InvoiceIcon,
  FilterList as FilterIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as ExportIcon,
  DateRange as DateRangeIcon,
  Receipt as BillsIcon,
  LocalShipping as VendorsIcon,
  Payments as PaymentsIcon,
  ShoppingBasket as PurchaseOrdersIcon,
} from '@mui/icons-material';
import { purchaseService } from '../utils/apiService';
import ExportButton from '../components/ExportButton';
import Loader from '../components/Loader';
import { format } from 'date-fns';
import image1 from '../assets/riyal.png'

function Purchases() {
  const [invoices, setInvoices] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    paymentStatus: {
      paid: true,
      pending: true,
      overdue: true,
      draft: true,
    }
  });

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Define export columns for the ExportButton component
  const exportColumns = [
    { field: 'purchaseNumber', headerName: 'Purchase #', width: 150 },
    { field: 'supplierName', headerName: 'Supplier', width: 200 },
    { field: 'issueDate', headerName: 'Date', width: 120 },
    { field: 'dueDate', headerName: 'Due Date', width: 120 },
    { field: 'totalAmount', headerName: 'Amount', width: 120 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'paymentStatus', headerName: 'Payment Status', width: 150 },
    { field: 'notes', headerName: 'Notes', width: 200 }
  ];

  // Format data for export
  const getExportData = () => {
    return filteredInvoices.map(invoice => ({
      purchaseNumber: invoice.purchaseNumber || '',
      supplierName: invoice.supplierId?.name || invoice.supplier?.name || 'Walk-in Supplier',
      issueDate: invoice.issueDate ? format(new Date(invoice.issueDate), 'dd/MM/yyyy') : '',
      dueDate: invoice.dueDate ? format(new Date(invoice.dueDate), 'dd/MM/yyyy') : '',
      totalAmount: invoice.totalAmount ? Number(invoice.totalAmount).toFixed(2) : '0.00',
      status: invoice.status || '',
      paymentStatus: invoice.paymentStatus || '',
      notes: invoice.notes || ''
    }));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...value,
      },
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = async (id) => {
    try {
      setDeleting(true);
      // Call API to delete the purchase
      await purchaseService.deletePurchase(id);

      // Update local state to remove the deleted purchase
      setInvoices(prevInvoices => prevInvoices.filter(invoice => invoice._id !== id));

      // Show success notification
      setNotification({
        open: true,
        message: 'Purchase invoice deleted successfully',
        severity: 'success'
      });
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting purchase:', error);
      setNotification({
        open: true,
        message: 'Error deleting purchase: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (id) => {
    navigate(`/purchase-invoice/${id}`);
  };

  useEffect(() => {
    const fetchPurchases = async () => {
      setLoading(true);
      try {
        const response = await purchaseService.getPurchases();
        console.log('Fetched purchases response:', response);

        // Extract purchases from response.purchases or use response directly if it's an array
        const data = response?.purchases || response;
        setInvoices(Array.isArray(data) ? data : []);
        console.log('Invoices set to:', data);
      } catch (error) {
        console.error('Error fetching purchases:', error);
        setNotification({
          open: true,
          message: 'Error fetching purchases: ' + (error.message || 'Unknown error'),
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, []);

  const filteredInvoices = Array.isArray(invoices) ? invoices.filter(invoice => {
    // Payment Status filter - check paymentStatus field
    const statusMatch =
      (filters.paymentStatus.paid && (invoice.paymentStatus === 'Paid' || invoice.paymentStatus === 'paid')) ||
      (filters.paymentStatus.pending && (invoice.paymentStatus === 'Pending' || invoice.paymentStatus === 'pending' || invoice.paymentStatus === 'Not Paid')) ||
      (filters.paymentStatus.overdue && (invoice.paymentStatus === 'Overdue' || invoice.paymentStatus === 'overdue')) ||
      (filters.paymentStatus.draft && (
        invoice.paymentStatus === 'Partially Paid' ||
        invoice.paymentStatus === 'partial' ||
        invoice.status === 'Draft' ||
        invoice.status === 'draft' ||
        invoice.status === 'Submitted'
      ));

    // Search filter
    const searchMatch = searchQuery === '' ||
      invoice.purchaseNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.supplierId?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.supplier?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    return statusMatch && searchMatch;
  }) : [];

  const paginatedInvoices = filteredInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Purchases
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Manage your purchases and invoices
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ExportButton
            columns={exportColumns}
            data={getExportData()}
            filename="purchase-invoices"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => navigate('/purchases/new-invoice')}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            New Invoice
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="Invoices" />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search for invoices..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            maxWidth: 500,
            height: 28,
            '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              height: 28,
            },
            '& .MuiInputBase-input': {
              py: 0.5,
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{
            borderRadius: '2px',
            textTransform: 'none',
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
          Filters
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              width: 200,
              p: 1,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Payment Status</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.paymentStatus.paid}
                  onChange={(e) => handleFilterChange('paymentStatus', { paid: e.target.checked })}
                  size="small"
                />
              }
              label="Paid"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.paymentStatus.pending}
                  onChange={(e) => handleFilterChange('paymentStatus', { pending: e.target.checked })}
                  size="small"
                />
              }
              label="Pending"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.paymentStatus.overdue}
                  onChange={(e) => handleFilterChange('paymentStatus', { overdue: e.target.checked })}
                  size="small"
                />
              }
              label="Overdue"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.paymentStatus.draft}
                  onChange={(e) => handleFilterChange('paymentStatus', { draft: e.target.checked })}
                  size="small"
                />
              }
              label="Draft"
            />
          </FormGroup>
        </Menu>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>Invoice #</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Date</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Supplier</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Subtotal</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>VAT Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Total Amount</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && paginatedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Loader
                        loading={true}
                        size="medium"
                        text="Loading purchases..."
                      />
                    </TableCell>
                  </TableRow>
                ) : paginatedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No purchase invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvoices.map((invoice) => (
                    <TableRow
                      key={invoice._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          {invoice.purchaseNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        {new Date(invoice.issueDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        {invoice.supplierId?.name || invoice.supplier?.name || 'Walk-in Supplier'}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} />{invoice.subtotal?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} /> {invoice.taxAmount?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} /> {invoice.totalAmount?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={invoice.paymentStatus}
                          size="small"
                          color={
                            invoice.paymentStatus === 'Paid' || invoice.paymentStatus === 'paid' ? 'success' :
                              invoice.paymentStatus === 'Pending' || invoice.paymentStatus === 'pending' || invoice.paymentStatus === 'Not Paid' ? 'warning' :
                                invoice.paymentStatus === 'Overdue' || invoice.paymentStatus === 'overdue' ? 'error' :
                                  invoice.paymentStatus === 'Partially Paid' ? 'info' :
                                    'default'
                          }
                          variant="outlined"
                          sx={{
                            fontWeight: 'medium',
                            backgroundColor:
                              invoice.paymentStatus === 'Paid' || invoice.paymentStatus === 'paid' ? 'rgba(46, 205, 111, 0.1)' :
                                invoice.paymentStatus === 'Pending' || invoice.paymentStatus === 'pending' || invoice.paymentStatus === 'Not Paid' ? 'rgba(255, 193, 7, 0.1)' :
                                  invoice.paymentStatus === 'Overdue' || invoice.paymentStatus === 'overdue' ? 'rgba(211, 47, 47, 0.1)' :
                                    invoice.paymentStatus === 'Partially Paid' ? 'rgba(33, 150, 243, 0.1)' :
                                      'rgba(0, 0, 0, 0.08)'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleEditClick(invoice._id)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => {
                              setPurchaseToDelete(invoice);
                              setDeleteDialogOpen(true);
                            }}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}

              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Purchase Invoice?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the purchase invoice for {purchaseToDelete?.purchaseNumber}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Loader
            loading={deleting}
            size="small"
            text="Deleting..."
            position="left"
            buttonProps={{
              color: "error",
              variant: "contained",
              onClick: () => {
                handleDeleteClick(purchaseToDelete?._id);
              }
            }}
          >
            Delete
          </Loader>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Purchases; 